// 수면 요약·점수 계산.
//
// 앱(SmartRingSleepScore.kt · SmartRingSleepSummary.kt)의 산식을 그대로 옮긴 것이다.
// 같은 밤에 대해 앱과 센터가 다른 점수를 보여주면 보호자와 사용자가 서로 다른
// "오늘의 수면"을 갖게 되므로, 산식을 바꿀 때는 반드시 양쪽을 함께 바꾼다.
//
// 세그먼트 sdkType 은 QcRing SDK 의 재매핑 값이다(worker payloadDetails.ts 와 동일):
//   1 DEEP · 2 LIGHT · 3 RESTLESS(짧은 각성) · 4 REM · 5 AWAKE(지속 각성)
// RESTLESS 와 AWAKE 는 둘 다 '자지 않은 시간'이지만 링 집계에서 취급이 달라
// 구분해 둔다 — AWAKE 를 버리면 실제 깨어 있던 시간이 통째로 사라진다.

export const SLEEP_STAGES = {
    DEEP: 'DEEP',
    LIGHT: 'LIGHT',
    RESTLESS: 'RESTLESS',
    REM: 'REM',
    AWAKE: 'AWAKE'
};

const STAGE_BY_SDK_TYPE = {
    1: SLEEP_STAGES.DEEP,
    2: SLEEP_STAGES.LIGHT,
    3: SLEEP_STAGES.RESTLESS,
    4: SLEEP_STAGES.REM,
    5: SLEEP_STAGES.AWAKE
};

// 화면이 "획득/만점"을 보여주므로 내보낸다. 앱과 같은 값이어야 한다.
export const GOAL_MINUTES = 8 * 60;
export const DURATION_MAX = 50;
export const EFFICIENCY_MAX = 30;
export const CONTINUITY_MAX = 20;

const EFFICIENCY_FULL_PERCENT = 85;
const EFFICIENCY_ZERO_PERCENT = 50;
const FREE_AWAKENINGS = 1;
const PENALTY_PER_AWAKENING = 4;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const isAsleep = (stage) => (
    stage === SLEEP_STAGES.DEEP || stage === SLEEP_STAGES.LIGHT || stage === SLEEP_STAGES.REM
);

// 수면을 '끊은' 각성만 센다. 잠들기 전 뒤척임과 기상 직전 각성은 제외한다.
// (앱 SmartRingSleepScore.countAwakenings 와 동일. 링의 wakingCount 는
// 지속 각성을 세지 않아 쓰지 않는다.)
export const countAwakenings = (timeline) => {
    if (timeline.length < 3) return 0;

    let count = 0;
    for (let index = 1; index < timeline.length - 1; index += 1) {
        if (isAsleep(timeline[index].stage)) continue;

        const sleepBefore = timeline.slice(0, index).some((entry) => isAsleep(entry.stage));
        const sleepAfter = timeline.slice(index + 1).some((entry) => isAsleep(entry.stage));
        if (sleepBefore && sleepAfter) count += 1;
    }
    return count;
};

// 시간 50 + 효율 30 + 연속성 20.
// 효율·연속성은 수면 시간 달성률로 다시 스케일한다 — 30분 낮잠이
// 효율·연속성 만점을 그대로 챙기는 것을 막는다(앱 주석 참고).
export const calculateScore = ({ sleepMinutes, efficiencyPercent, awakeningCount }) => {
    const durationPoints = clamp(
        Math.round((sleepMinutes / GOAL_MINUTES) * DURATION_MAX), 0, DURATION_MAX
    );

    const durationRatio = clamp(sleepMinutes / GOAL_MINUTES, 0, 1);

    const efficiencyRatio = clamp(
        (efficiencyPercent - EFFICIENCY_ZERO_PERCENT) /
            (EFFICIENCY_FULL_PERCENT - EFFICIENCY_ZERO_PERCENT),
        0, 1
    );
    const efficiencyPoints = clamp(
        Math.round(efficiencyRatio * EFFICIENCY_MAX * durationRatio), 0, EFFICIENCY_MAX
    );

    const penalty = Math.max(awakeningCount - FREE_AWAKENINGS, 0) * PENALTY_PER_AWAKENING;
    const continuityRatio = clamp(CONTINUITY_MAX - penalty, 0, CONTINUITY_MAX) / CONTINUITY_MAX;
    const continuityPoints = clamp(
        Math.round(continuityRatio * CONTINUITY_MAX * durationRatio), 0, CONTINUITY_MAX
    );

    return {
        durationPoints,
        efficiencyPoints,
        continuityPoints,
        total: durationPoints + efficiencyPoints + continuityPoints
    };
};

export const qualityLabel = (score) => {
    if (score <= 49) return '수면부족';
    if (score <= 79) return '보통';
    return '좋음';
};

/**
 * 서버 daily 응답의 sleep.segments 로 수면 요약을 만든다.
 * 세그먼트가 없거나 전부 무효면 null.
 *
 * 서버가 저장해 둔 단계별 합계(awake_seconds 등)를 쓰지 않고 세그먼트에서
 * 다시 합산한다 — SDK 집계는 지속 각성(AWAKE)을 빼고 세기 때문에 저장값을
 * 믿으면 앱 화면과 어긋난다.
 */
export const summarizeSleep = (segments) => {
    if (!Array.isArray(segments)) return null;

    const timeline = segments
        .map((segment) => {
            const stage = STAGE_BY_SDK_TYPE[segment.sdkType];
            if (!stage) return null;

            const startSeconds = Date.parse(segment.startedAt) / 1000;
            const endSeconds = Date.parse(segment.endedAt) / 1000;
            if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds)) return null;
            if (endSeconds <= startSeconds) return null;

            return { startSeconds, endSeconds, stage };
        })
        .filter(Boolean)
        .sort((a, b) => a.startSeconds - b.startSeconds);

    if (timeline.length === 0) return null;

    const startSeconds = timeline[0].startSeconds;
    const endSeconds = Math.max(...timeline.map((entry) => entry.endSeconds));
    const periodSeconds = endSeconds - startSeconds;
    if (periodSeconds <= 0) return null;

    // 초 단위로 합산한 뒤 마지막에 한 번만 분으로 바꾼다.
    // 단계별로 먼저 내림하면 오차가 합계와 효율에 그대로 실린다.
    const stageSeconds = (stage) => timeline
        .filter((entry) => entry.stage === stage)
        .reduce((sum, entry) => sum + (entry.endSeconds - entry.startSeconds), 0);

    const restlessSeconds = stageSeconds(SLEEP_STAGES.RESTLESS);
    const awakeOnlySeconds = stageSeconds(SLEEP_STAGES.AWAKE);
    const remSeconds = stageSeconds(SLEEP_STAGES.REM);
    const lightSeconds = stageSeconds(SLEEP_STAGES.LIGHT);
    const deepSeconds = stageSeconds(SLEEP_STAGES.DEEP);
    const sleepSeconds = remSeconds + lightSeconds + deepSeconds;

    const sleepMinutes = Math.floor(sleepSeconds / 60);
    const efficiencyPercent = clamp(
        Math.round((sleepSeconds * 100) / periodSeconds), 0, 100
    );
    const awakeningCount = countAwakenings(timeline);
    const scoreBreakdown = calculateScore({ sleepMinutes, efficiencyPercent, awakeningCount });

    return {
        startSeconds,
        endSeconds,
        periodMinutes: Math.floor(periodSeconds / 60),
        sleepMinutes,
        awakeMinutes: Math.floor(awakeOnlySeconds / 60),
        restlessMinutes: Math.floor(restlessSeconds / 60),
        remMinutes: Math.floor(remSeconds / 60),
        lightMinutes: Math.floor(lightSeconds / 60),
        deepMinutes: Math.floor(deepSeconds / 60),
        efficiencyPercent,
        awakeningCount,
        scoreBreakdown,
        score: scoreBreakdown.total,
        quality: qualityLabel(scoreBreakdown.total),
        timeline
    };
};
